import { GoogleMap } from "@react-google-maps/api";
import { useEffect, useRef } from "react";
import type { CSSProperties, ReactNode } from "react";
import type { GeoPoint } from "./api";
import type { FuelPlan } from "./services";

const mapContainerStyle: CSSProperties = {
  width: "100%",
  height: "100%",
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0
};

interface MapProps {
  children?: ReactNode;
  onCenterChanged?: () => void;
  center?: GeoPoint;
  onLoad?: (map: google.maps.Map) => void;
  routes?: FuelPlan | null;
}

const Map = ({ children = <></>, onCenterChanged, center, onLoad, routes = null }: MapProps) => {
  const mapRef = useRef<google.maps.Map | null>(null);

  const handleLoad = (map: google.maps.Map) => {
    mapRef.current = map;
    if (onLoad) onLoad(map);
  };

  // Function to calculate bounds for all routes and markers
  const calculateBounds = (map: google.maps.Map, routes: FuelPlan | null) => {
    if (!map || !routes) return;

    const bounds = new window.google.maps.LatLngBounds();
    const routeArray = Array.isArray(routes) ? routes : [routes];

    routeArray.forEach(route => {
      // Add origin and destination - these will be the farthest points
      bounds.extend(new window.google.maps.LatLng(route.origin.lat, route.origin.lng));
      bounds.extend(new window.google.maps.LatLng(route.destination.lat, route.destination.lng));
    });

    // Add padding to the bounds.
    // NOTE: google.maps.Map.fitBounds expects a `Padding` ({ top, right, bottom,
    // left }) or a number as the second arg, but this wraps it in an extra
    // `{ padding: ... }` object, so the padding is effectively ignored at runtime.
    // Suspected bug preserved (cast) rather than changed during the TS migration.
    map.fitBounds(bounds, {
      padding: {
        top: 50,
        right: 50,
        bottom: 50,
        left: 50
      }
    } as unknown as google.maps.Padding);
  };

  // Effect to fit bounds when routes change
  useEffect(() => {
    if (routes && mapRef.current) {
      calculateBounds(mapRef.current, routes);
    }
  }, [routes]);

  return (
    <>
      <GoogleMap
        onLoad={handleLoad}
        center={center}
        zoom={7}
        onDragEnd={onCenterChanged}
        mapContainerStyle={mapContainerStyle}
      >
        {children}
      </GoogleMap>
    </>
  );
};

export default Map;
